import React, { useContext, useEffect, useReducer, useRef, useState } from 'react';
import { bool, node, string } from 'prop-types';
import { Button } from '@thd-olt-component-react/button';
import { Heading } from '@thd-olt-component-react/heading';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { CustomerImageCarousel } from './CustomerImageCarousel/CustomerImageCarousel.component';
import { Col, Row } from '@thd-olt-component-react/grid';
import { PresentationProvider } from '../context/PresentationProvider'
import { track } from '../ratings-reviews-analytics';
import { Filter } from './Filter/Filter.component';
import { FirstReview } from './FirstReview/FirstReview.component';
import { RatingsAndReviewsMobile } from './Mobile/RatingsAndReviewsMobile';
import { Review } from './Review/Review.component';
import { ReviewPager } from './Paging/ReviewPager.component';
import { ReviewSentiments } from './Filter/components/ReviewSentiments/ReviewSentiments.component';
import { WriteAReview } from './WriteAReview/WriteAReview.component';
import { Overview } from './Overview/Overview';
import { Search, Sort } from './Filter/components';
import './ratings-and-reviews.style.scss';
import { useProductReviews } from './useProductReviews';

// TODO: Move to a constants file or similar
const DEFAULT_FILTERS =  [
  {
    checked: false,
    id: 'rating_5',
    value: '5',
    count: 0
  },
  {
    checked: false,
    id: 'rating_4',
    value: '4',
    count: 0
  },
  {
    checked: false,
    id: 'rating_3',
    value: '3',
    count: 0
  },
  {
    checked: false,
    id: 'rating_2',
    value: '2',
    count: 0
  },
  {
    checked: false,
    id: 'rating_1',
    value: '1',
    count: 0
  },
  {
    checked: false,
    id: 'verified',
    value: 'verified',
    count: 0
  }
];
const DEFAULT_PAGE_CONTEXT = {
  totalPages: 3,
  currentPage: 1,
  pageNeighbours: 1
};
const DEFAULT_SORTBY = 'photoreview';
const initialState = {
  filters: [...DEFAULT_FILTERS],
  firstPageReviews: {},
  pageContext: DEFAULT_PAGE_CONTEXT,
  productStatistics: {},
  searchText: null,
  selectedSentiment: null,
  sortBy: DEFAULT_SORTBY
};

const stateReducer = (state, action) => {
  const { type, value } = action;
  const { filters, selectedSentiment, pageContext, searchText } = state;
  switch (type) {
    case 'filter':
      pageContext.currentPage = 1;
      const updatedFilters = filters.map((filter) => {
        return {
          checked: filter.value === value.toString() ? !filter.checked : filter.checked,
          id: filter.id,
          value: filter.value,
          count: filter.count
        };
      });
      const isChecked = updatedFilters.find((filter) => filter.value === value.toString())?.checked;
      if (isChecked) {
        track('event', {
          origin: 'ratings and reviews',
          value: value,
          location: type
        });
      }
      return {
        ...state,
        pageContext,
        filters: updatedFilters,
        selectedSentiment: null,
        searchText: searchText
      };
    case 'removeFilter':
      pageContext.currentPage = 1;
      const updatedSentiment = value.Feature ? null : selectedSentiment;
      let removeUpdatedFilters = filters;
      if (!value.Feature) {
        removeUpdatedFilters = filters.map((filter) => {
          return {
            checked: filter.value === value.value ? !filter.checked : filter.checked,
            id: filter.id,
            value: filter.value,
            count: filter.count
          };
        });
      }
      return {
        ...state,
        pageContext,
        filters: removeUpdatedFilters,
        selectedSentiment: updatedSentiment
      };
    case 'pageChange':
      return {
        ...state,
        pageContext: value
      };
    case 'search':
      pageContext.currentPage = 1;
      return {
        ...state,
        pageContext,
        searchText: value
      };
    case 'sentiment':
      // on sentiment change reset other filters, if the sentiment is currently selected reset that as well
      const { Feature } = value;
      return {
        ...state,
        filters: [...DEFAULT_FILTERS],
        pageContext:DEFAULT_PAGE_CONTEXT,
        searchText: null,
        selectedSentiment: Feature === selectedSentiment?.Feature ? null : value,
        sortBy: DEFAULT_SORTBY
      };
    case 'sort':
      pageContext.currentPage = 1;
      return {
        ...state,
        pageContext,
        sortBy: value
      };
    case 'totalPages':
      pageContext.totalPages = value;
      return {
        ...state,
        pageContext
      };
    default:
      console.error('Unhandled Action: ', action);
  }
};

export const RatingsAndReviews = (props) => {
  const {
    apiKey = '', children, collectionId, itemId, prosAndCons, target, forceCondensedLayout
  } = props;
  const [state, dispatch] = useReducer(stateReducer, initialState);
  const persistedStatistics = useRef({});
  const initialReviews = useRef({});
  const persistedPageContext = useRef();
  const carouselRef = useRef();
  const { averageRating, FilterSelected, loading, reviewModels = [], statistics, totalReviews } = useProductReviews({ itemId, ...state });
  useEffect(() => {
    if (!persistedPageContext.current) {
      persistedPageContext.current = state.pageContext;
      return;
    }

    if (persistedPageContext.current.currentPage !== state.pageContext.currentPage) {
      persistedPageContext.current = state.pageContext;
      if (carouselRef.current) {
        carouselRef.current.scrollIntoView({
          behavior: 'auto' // do not use smooth. It'll cause the page to scroll to the bottom while it updates the results.
        });
      }
    }
  }, [state.pageContext]);

  const { channel } = useContext(ExperienceContext);
  const useCondensedLayout = forceCondensedLayout || channel === 'mobile';
  const { filters, pageContext, searchText, selectedSentiment, sortBy } = state;
  const hasProductData = averageRating || totalReviews;
  if (loading && !persistedStatistics.current[itemId]) {
    return null;
  }
  const selected = selectedSentiment ? [selectedSentiment] : filters.filter((rf) => rf.checked);
  let productReviews = reviewModels.length || searchText || selected.length ? reviewModels : initialReviews.current[itemId] || [];
  const noFallbacks = !(hasProductData || productReviews || persistedStatistics.current[itemId]);
  if (noFallbacks) {
    return null;
  }
  const updatedTotalpage = statistics?.totalPages ? statistics.totalPages : 1;
  if(state.pageContext.totalPages !== updatedTotalpage) {
    dispatch({type: 'totalPages', value: updatedTotalpage});
  }
  const { SearchText } = FilterSelected || {};
  if (persistedStatistics.current && !persistedStatistics.current[itemId]) {
    persistedStatistics.current[itemId] = statistics;
    initialReviews.current[itemId] = reviewModels;
  }
  const reviewStatistics = persistedStatistics.current && persistedStatistics.current[itemId] ? persistedStatistics.current[itemId] : statistics;

  const renderRatingsAndReviews = () => {
    if (useCondensedLayout) {
      // mobile renders first 3 reviews
      const firstThreeReviews = reviewModels.slice(0, 3).filter(review => !!review);
      return (
        <div className="ratings-reviews" id="ratings-and-reviews">
          <RatingsAndReviewsMobile
            apiKey={apiKey}
            filterCheckboxes={filters}
            itemId={itemId}
            onDeselectFilter={(checkbox) => dispatch({ type: 'removeFilter', value: checkbox})}
            onFilterChange={(updatedFilters) => dispatch({type: 'filter', value: updatedFilters})}
            mobileReviewsOnFirstPage={firstThreeReviews}
            onSearch={(searchTerm) => dispatch({type: 'search', value: searchTerm})}
            onPageChange={(updatedPageContext) => dispatch({
              type: 'pageChange',
              value: updatedPageContext
            })}
            onSortChange={(updatedSortValue) => dispatch({type: 'sort', value: updatedSortValue})}
            searchTerm={SearchText}
            selected={selected}
            sortBy={sortBy}
            reviewModels={reviewModels}
            reviewStatistics={reviewStatistics}
            selectedSentiment={selectedSentiment}
            target={target}
          >
            {children}
          </RatingsAndReviewsMobile>
        </div>
      );
    }

    if (hasProductData && !(searchText || productReviews.length || selected.length)) {
     return (
       <div className="ratings-reviews" id="ratings-and-reviews">
         <Heading title="Customer Reviews" underline/>
         <Overview reviewStatistics={{}} itemId={itemId} />
         <WriteAReview apiKey={apiKey} itemId={itemId} totalReviews={totalReviews} />
       </div>
     )
    }

    if (!(productReviews.length || persistedStatistics.current[itemId]?.length || searchText || selected.length)) {
      return (<FirstReview
        apiKey={apiKey}
        itemId={itemId}
        reviewStatistics={reviewStatistics}
      />);
    }

    return (
      <div className="ratings-reviews" id="ratings-and-reviews">
        <Heading title="Customer Reviews" underline/>
        <Overview
          clickableFilters
          itemId={itemId}
          reviewStatistics={reviewStatistics}
          filterCheckboxes={filters}
          onFilterChange={(updatedFilters) => dispatch({type: 'filter', value: updatedFilters})}
          selectedSentiment={selectedSentiment}
        />
        {prosAndCons && (
          <ReviewSentiments
            itemId={itemId}
            selectedSentiment={selectedSentiment}
            onSentimentChange={(sentiment) => dispatch({type: 'sentiment', value: sentiment})}
          />
        )}
        <div className="customer-image-carousel__wrapper" ref={carouselRef}>
          <CustomerImageCarousel itemId={itemId}/>
        </div>

        {selected.length > 0
        && (
          <Col xs="12">
            <h3>{selected.length === 1 ? 'Your Selection:' : 'Your Selections:'}</h3>
            {selected
              .map((checkbox, checkboxIndex) => (
                <Button
                  key={checkboxIndex}
                  id={checkbox.id}
                  onClick={() => dispatch({ type: 'removeFilter', value: checkbox})}
                  filter
                >
                  {selectedSentiment ? selectedSentiment.Feature : `Ratings ${checkbox.value}`}
                </Button>
              ))}
          </Col>
        )}
        {!selectedSentiment
        && (
          <>
            <Row className="ratings-reviews__search-and-sort">
              <Col xs={12} sm={12} md={4} lg={4}
                   className="ratings-and-reviews__search-and-filter">
                <Search onSearch={(searchTerm) => dispatch({ type: 'search', value: searchTerm})} searchTerm={SearchText}
                        noResults={state.searchText && reviewModels.length === 0 && !loading}/>
              </Col>
              <Col xs={12} sm={12} md={5} lg={5}
                   className="ratings-and-reviews__search-and-filter">
                <Filter
                  onChange={(updatedFilters) => dispatch({ type: 'filter', value: updatedFilters})}
                  ratingFilters={filters}
                  reviewStatistics={reviewStatistics}
                  selectedSentiment={selectedSentiment}
                />
              </Col>
              <Col xs={12} sm={12} md={3} lg={3}>
                <Sort onChange={(newSort) => dispatch({ type: 'sort', value: newSort})} sortBy={sortBy} />
              </Col>
            </Row>
          </>
        )}


        {(productReviews).map((review, reviewIndex) => {
          return (
            <Review
              review={review}
              id={reviewIndex}
              key={`review-${reviewIndex}`}
              searchTerm={SearchText}
              apiKey={apiKey}
            />
          );
        })}
        <Row>
          <Col xs="6" sm="12" md="6">
            <div className="ratings-reviews__review-pager">
              <ReviewPager
                itemId={itemId}
                reviewsPageContext={pageContext}
                onPageChanged={(newPageContext) => dispatch({ type: 'pageChange', value: newPageContext })}
              />
            </div>
          </Col>
          <Col fallback="12" xs="12" sm="12" md="6">
            <Row className="write-review">
              <Col fallback="12" xs="4" sm="6" md="6">
                <div className="write-review-header">Review this Product</div>
                <div className="write-review-thoughts-text">Share your thoughts with other customers
                </div>
              </Col>
              <Col fallback="12" xs="6">
                <div className="write-review">
                  <div className="write-review-button-mobile-expanded">
                    <WriteAReview
                      apiKey={apiKey}
                      reviewStatistics={reviewStatistics}
                      itemId={itemId}
                    />
                  </div>
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <PresentationProvider useCondensedLayout={useCondensedLayout}>
      {renderRatingsAndReviews()}
    </PresentationProvider>
  )
};

RatingsAndReviews.displayName = 'RatingsAndReviews';

RatingsAndReviews.propTypes = {
  /*
    This is a bad practice.
    APIs should not be called directly.
    There is a story to refactor this to use a graphql mutation:
    https://www.pivotaltracker.com/story/show/173363061
  */
  apiKey: string,
  children: node,
  collectionId: string,
  itemId: string.isRequired,
  prosAndCons: bool,
  forceCondensedLayout: bool,
  target: string
};

RatingsAndReviews.defaultProps = {
  apiKey: 'x5w9jA8tWVGcqRhujrHTvjRwQfH8MkFc',
  children: null,
  collectionId: null,
  prosAndCons: false,
  forceCondensedLayout: false,
  target: ''
};
